import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import React from "react";
import { usePhoneFold } from "kontrolEdge/components/Shared/DetailSummary/usePhoneFold";


interface StatusEntry {
  status: number;
  text: string;
  createdAt: string;
}

interface StatusTableProps {
  data: StatusEntry[];
  headerContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  wrapperClassName?: string;
  /**
   * Width of the card itself. Defaults to three quarters, which suits the
   * detail page where this sits beside other content; a modal passes
   * `w-full` because there is nothing to sit beside and the extra quarter is
   * what pushed the row into horizontal scroll.
   */
  contentClassName?: string;
  /**
   * Start folded on a phone, its header showing the latest event (CTX-1421).
   * For the detail page, where it is the last of several cards; a modal that
   * exists to show the history leaves it open.
   */
  foldOnPhone?: boolean;
}

type StatusVariant = "success" | "neutral" | "warning";

const statusVariantMap: Record<number, StatusVariant> = {
  2: "success",  // Finished
  1: "neutral",  // Active
  3: "warning",  // Pending
};

/** The timeline dot takes the tag's colour, so the two layouts read the same. */
const DOT_COLOR: Record<StatusVariant, string> = {
  success: "bg-semantic-fg-success",
  neutral: "bg-semantic-fg-secondary",
  warning: "bg-semantic-fg-warning",
};

const TIME_ZONE = "America/Mexico_City";
const DATE_FORMAT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: TIME_ZONE,
});
const TIME_FORMAT = new Intl.DateTimeFormat("en-GB", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: TIME_ZONE,
});

/**
 * "Mar 25, 2025 · 06:00". The old "25/03/2025, 06:00 GMT-6" put the zone on
 * every row and a day/month order an English UI reads both ways; the zone is
 * now said once, in the header (CTX-1339).
 */
export function formatStatusDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return `${DATE_FORMAT.format(date)} · ${TIME_FORMAT.format(date)}`;
}

const StatusTable: React.FC<StatusTableProps> = ({
  data,
  headerContent = "Status",
  rightContent = "Mexico City time",
  wrapperClassName = "justify-start",
  contentClassName = "w-full md:w-3/4",
  foldOnPhone = false, }) => {
  const latest = data?.[data.length - 1];
  const fold = usePhoneFold(
    { header: headerContent, items: [] },
    { isCollapsible: true, initialExpanded: true },
    latest ? `${latest.text} · ${formatStatusDate(latest.createdAt)}` : undefined
  );
  const cardHeader = foldOnPhone
    ? fold
    : { isCollapsible: true, initialExpanded: true, headerContent };
  return (
    <div className={`flex ${wrapperClassName} py-4`}>
      <div className={contentClassName}>
      <GenericCard
        {...cardHeader}
        rightContent={
          rightContent ? <span className="text-xs font-normal text-semantic-text-secondary sm:text-sm">{rightContent}</span> : rightContent
        }
        bodyContent={
          // Wide screens: one row per status, tag left and date right. A phone
          // has no room for both side by side ("Port configuration in progress"
          // ran into its date), so there the list becomes a vertical timeline:
          // a dot per status on a rail, the name, and the date under it.
          <ol>
            {data?.map((entry, itemIndex) => {
              const variant = statusVariantMap[entry.status] ?? "neutral";
              const isLast = itemIndex === data.length - 1;
              return (
                <li
                  key={itemIndex}
                  className="relative flex flex-col gap-0.5 pb-4 pl-6 last:pb-1 sm:flex-row sm:flex-nowrap sm:items-center sm:gap-4 sm:border-b sm:py-2 sm:pl-0 sm:last:border-0"
                >
                  {!isLast && (
                    // The rail takes its status's colour, faded: the border tokens
                    // are too light to read as a line on the card.
                    <span
                      aria-hidden
                      className={`absolute -bottom-[7px] left-[4.5px] top-[13px] w-0.5 rounded-full opacity-40 sm:hidden ${DOT_COLOR[variant]}`}
                    />
                  )}
                  <span
                    aria-hidden
                    className={`absolute left-0 top-[7px] h-[11px] w-[11px] rounded-full ring-2 ring-semantic-bg-primary sm:hidden ${DOT_COLOR[variant]}`}
                  />
                  <div className="min-w-0 sm:flex-1">
                    <span className="text-sm font-medium text-semantic-text-primary sm:hidden">{entry.text}</span>
                    <div className="hidden sm:block">
                      <GenericTag minWidth="90px" label={String(entry.text)} variant={variant} className="whitespace-nowrap" />
                    </div>
                  </div>
                  <time
                    dateTime={entry.createdAt}
                    className="whitespace-nowrap text-xs text-semantic-text-secondary sm:flex-shrink-0 sm:text-right sm:text-sm"
                  >
                    {formatStatusDate(entry.createdAt)}
                  </time>
                </li>
              );
            })}
          </ol>
        }
      />
      </div>
    </div>
  );
};

export default StatusTable;

